import {
  Testimonials as TestimonialsUi,
  shuffleArray,
} from "@business-finland/wif-ui-lib/dist/Testimonials";
import type {
  ITestimonialCardProps,
  TestimonialsVariant,
} from "@business-finland/wif-ui-lib/dist/Testimonials";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { useMemo } from "react";

import HtmlParser from "@/components/HtmlParser";
import { Viewport } from "../../../constants/app";
import useEmployerSite from "../../../contexts/EmployerSiteContext";
import useImageDataHandler from "../../../hooks/useImageDataHandler";
import { LinkSwitchableField } from "../../../types/wif-next";
import { resolveCmsBackgroundColor } from "../../../util/cms-background-color";
import {
  jcrMultiFieldToArray,
  resolveLinkSwitchableField,
} from "../../../util/magnolia";

interface ITestimonialBullet {
  text?: string;
}

interface ITestimonialData {
  title: string;
  testimonialText?: string;
  personName?: string;
  companyName?: string;
  image?: Wif.Mgnl.Image;
  imageAltText?: string;
  body?: string;
  bullets?: Wif.Mgnl.NodeWithList<Wif.Mgnl.Node<ITestimonialBullet>>;
  ctaText?: string;
  ctaLink?: LinkSwitchableField;
}

interface ITestimonialProps {
  title: string;
  anchorTag?: string;
  backgroundColor?: "white" | "color";
  variant?: TestimonialsVariant | { field?: string };
  shuffle?: boolean | string;
  autoplay?: boolean | string;
  testimonials?: Wif.Mgnl.NodeWithList<Wif.Mgnl.Node<ITestimonialData>>;
}

export function resolveTestimonialsVariant(
  variant?: ITestimonialProps["variant"]
): TestimonialsVariant {
  const value = typeof variant === "string" ? variant : variant?.field;
  return value === "featured" ? "featured" : "quote";
}

export function isCmsFlagEnabled(
  value?: boolean | string,
  defaultValue = false
): boolean {
  if (value === undefined) return defaultValue;
  return value === true || value === "true";
}

function mapTestimonialImage(
  item: Wif.Mgnl.Node<ITestimonialData>,
  imageDataHandler: ReturnType<typeof useImageDataHandler>
): ITestimonialCardProps["image"] {
  const imageData = item.image
    ? imageDataHandler(item.image, { maxViewport: Viewport.lg })
    : null;

  const credits =
    typeof item.image === "string" ? "" : item.image?.metadata?.rights ?? "";

  if (!imageData) {
    return null;
  }

  return {
    src: imageData.src,
    width: imageData.width,
    height: imageData.height,
    altText: item.imageAltText || "",
    imageLoader: imageData.imageLoader,
    credits,
  };
}

export function mapTestimonialCards(
  testimonials: ITestimonialProps["testimonials"],
  variant: TestimonialsVariant,
  imageDataHandler: ReturnType<typeof useImageDataHandler>,
  isPreview?: boolean
): ITestimonialCardProps[] {
  if (!testimonials) {
    return [];
  }

  return jcrMultiFieldToArray(testimonials).map((item) => {
    const image = mapTestimonialImage(item, imageDataHandler);

    if (variant === "featured") {
      const resolvedLink = resolveLinkSwitchableField(item.ctaLink, isPreview);
      const fallbackBullets = item.bullets
        ? jcrMultiFieldToArray(item.bullets)
            .map((bullet) => bullet.text)
            .filter((text): text is string => Boolean(text))
        : [];
      const bodyHtml =
        item.body ||
        (fallbackBullets.length
          ? `<ul>${fallbackBullets
              .map((text) => `<li>${text}</li>`)
              .join("")}</ul>`
          : undefined);
      const cta =
        item.ctaText && resolvedLink.linkHref
          ? {
              text: item.ctaText,
              href: resolvedLink.linkHref,
              isExternalLink: resolvedLink.isExternalLink,
            }
          : undefined;

      return {
        title: item.title,
        image,
        body: bodyHtml ? <HtmlParser html={bodyHtml} /> : undefined,
        cta,
      };
    }

    return {
      title: item.title,
      testimonialText: item.testimonialText,
      personName: item.personName,
      companyName: item.companyName,
      image,
    };
  });
}

export function Testimonials({
  title,
  anchorTag,
  backgroundColor,
  variant,
  shuffle,
  autoplay,
  testimonials,
}: ITestimonialProps): JSX.Element | null {
  const imageDataHandler = useImageDataHandler();
  const { t } = useTranslation("common");
  const { isEmployerSite } = useEmployerSite();
  const { isPreview } = useRouter();
  const resolvedVariant = resolveTestimonialsVariant(variant);
  const shouldShuffle = isCmsFlagEnabled(
    shuffle,
    resolvedVariant === "featured"
  );

  const orderedCards = useMemo(() => {
    const cards = mapTestimonialCards(
      testimonials,
      resolvedVariant,
      imageDataHandler,
      isPreview
    );
    return shouldShuffle ? shuffleArray(cards) : cards;
  }, [
    testimonials,
    resolvedVariant,
    imageDataHandler,
    isPreview,
    shouldShuffle,
  ]);

  if (!orderedCards.length) {
    return null;
  }

  return (
    <TestimonialsUi
      id={anchorTag}
      title={title}
      testimonials={orderedCards}
      loadMoreText={t("pagination.loadMore")}
      backgroundColor={resolveCmsBackgroundColor(
        backgroundColor,
        isEmployerSite,
        "lightBlue"
      )}
      variant={resolvedVariant}
      autoplay={isCmsFlagEnabled(autoplay)}
    />
  );
}
